import React, { useRef, useState } from 'react';

import { Paper } from '@material-ui/core';
import MaterialTable, { MTableEditRow } from 'material-table';
import PropTypes from 'prop-types';

export function DynamicTable(props) {
  const { columns, editable, editableOptions = {}, loading, onRowsChanged, options, replaceComponents, rows, title } = props;
  const [tableInitialFormData, setTableInitialFormData] = useState({});
  const tableRef = useRef();

  function addNewRow(template = {}) {
    setTableInitialFormData(template);

    tableRef.current.dataManager.changeRowEditing();
    tableRef.current.setState({
      ...tableRef.current.dataManager.getRenderState(),
      showAddRow: true,
    });
  }

  const actions = [];

  if (editableOptions.duplicate) {
    actions.push({
      icon: 'library_add',
      tooltip: 'Duplicate',
      onClick: (_, rowData) => {
        addNewRow({ ...rowData });
      },
    });
  }

  const components = {
    Container: props => <Paper {...props} elevation={0}/>,
    EditRow: (props) =>
      <MTableEditRow {...props} onKeyDown={(e) => {
        if (e.keyCode === 27) {
          props.onEditingCanceled(props.mode, props.data);
        }
      }} />,
    ...replaceComponents,
  };

  const editableEvents = {
    ...(editable || {}),
  };

  if (editableOptions.add) {
    editableEvents.onRowAdd = async newData => {
      const updatedRows = [...rows, newData];
      onRowsChanged(updatedRows);

      if (editableOptions.continuous) {
        setTimeout(() => {
          addNewRow({});
        });
      }
    };
  }

  if (editableOptions.bulk) {
    editableEvents.onBulkUpdate = async changes => {
      const updatedRows = [...rows];

      for (const { newData, oldData } of Object.values(changes)) {
        const index = oldData.tableData.id;
        updatedRows[index] = newData;
      }

      onRowsChanged(updatedRows);
    };
  }

  if (editableOptions.delete) {
    editableEvents.onRowDelete = async oldData => {
      const updatedRows = [...rows];
      const index = oldData.tableData.id;
      updatedRows.splice(index, 1);

      onRowsChanged(updatedRows);
    };
  }

  if (editableOptions.single) {
    editableEvents.onRowUpdate = async (newData, oldData) => {
      const updatedRows = [...rows];

      const index = oldData.tableData.id;
      updatedRows[index] = newData;

      onRowsChanged(updatedRows);
    };
  }

  const defaultFormData = {};

  for (const column of columns) {
    if ('initialEditValue' in column && column.initialEditValue !== undefined) {
      defaultFormData[column.field] = column.initialEditValue;
    }
  }

  return (
    <MaterialTable
      actions={actions}
      columns={columns}
      components={components}
      data={rows}
      editable={editable === false ? undefined : editableEvents}
      localization={{
        header: {
          actions: '-',
        },
      }}
      initialFormData={{ ...defaultFormData, ...tableInitialFormData }}
      isLoading={loading}
      options={{
        ...options,
        actionsColumnIndex: -1,
        pageSize: 10,
        pageSizeOptions: [25, 50, 100],
        paging: false,
        search: false,
      }}
      tableRef={tableRef}
      title={title}
    />
  );
}

DynamicTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  editable: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
  ]),
  editableOptions: PropTypes.shape({
    add: PropTypes.bool,
    bulk: PropTypes.bool,
    continuous: PropTypes.bool,
    delete: PropTypes.bool,
    duplicate: PropTypes.bool,
    single: PropTypes.bool,
  }),
  loading: PropTypes.bool,
  onRowsChanged: PropTypes.func,
  replaceComponents: PropTypes.object,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
};

export function createColumn(name, options) {
  const { invalidTest, invalidText, ...rest } = options;

  return {
    title: name,
    ...rest,
    field: name,
    validate: row => {
      if (!invalidTest) {
        return true;
      }

      const invalid = invalidTest(row[name], row);

      return invalid
        ? { isValid: false, helperText: `${name} ${invalidText}` }
        : true;
    },
  };
}
